export const FOCUS_TAGLINE_PRESETS: Record<string, string> = {
  shipping_case_study: "Shipping my first case study",
  clarifying_next_move: "Clarifying my next move",
  building_customer_proof: "Building customer proof",
  mapping_first_jtbd: "Mapping my first job-to-be-done",
  validating_problem_space: "Validating my problem space",
  refining_value_prop: "Refining my value proposition",
  launching_mvp: "Launching an MVP",
  growing_user_base: "Growing my user base",
  learning_to_sell: "Learning to sell with stories",
  building_in_public: "Building in public",
};

export const MICRO_MANTRA_PRESETS: Record<string, string> = {
  proof_over_pitch: "Proof over pitch",
  ship_small_learn_fast: "Ship small, learn fast",
  progress_not_perfection: "Progress not perfection",
  talk_to_customers: "Talk to customers first",
  clarity_through_action: "Clarity through action",
  one_step_at_a_time: "One step at a time",
  done_beats_perfect: "Done beats perfect",
  build_measure_learn: "Build, measure, learn",
  stay_curious: "Stay curious, stay sharp",
  show_your_work: "Show your work",
};

export interface BannerPatternDef {
  id: string;
  label: string;
  emoji: string;
  description: string;
}

export const BANNER_PATTERNS: Record<string, BannerPatternDef> = {
  rocket_grid: { id: "rocket_grid", label: "Rocket Grid", emoji: "🚀", description: "Rockets in a clean grid" },
  lightbulb_wave: { id: "lightbulb_wave", label: "Lightbulb Wave", emoji: "💡", description: "Lightbulbs in a flowing wave" },
  target_ring: { id: "target_ring", label: "Target Ring", emoji: "🎯", description: "Targets in concentric clusters" },
  puzzle_flow: { id: "puzzle_flow", label: "Puzzle Flow", emoji: "🧩", description: "Puzzle pieces scattered" },
  star_burst: { id: "star_burst", label: "Star Burst", emoji: "⭐", description: "Stars radiating outward" },
  book_stack: { id: "book_stack", label: "Book Stack", emoji: "📚", description: "Books in neat columns" },
  fire_trail: { id: "fire_trail", label: "Fire Trail", emoji: "🔥", description: "Fire along a diagonal" },
  compass_scatter: { id: "compass_scatter", label: "Compass Scatter", emoji: "🧭", description: "Compasses scattered randomly" },
};

export const BANNER_PATTERN_IDS = Object.keys(BANNER_PATTERNS);

export interface ThemeColorDef {
  label: string;
  bgClass: string;
  hex: string;
}

export const THEME_COLORS: Record<string, ThemeColorDef> = {
  blue: { label: "Blue", bgClass: "bg-blue-500", hex: "#3b82f6" },
  red: { label: "Red", bgClass: "bg-red-500", hex: "#ef4444" },
  green: { label: "Green", bgClass: "bg-emerald-500", hex: "#10b981" },
  yellow: { label: "Yellow", bgClass: "bg-amber-400", hex: "#fbbf24" },
  light_gray: { label: "Light Gray", bgClass: "bg-gray-300", hex: "#d1d5db" },
};

export const THEME_COLOR_VALUES = Object.keys(THEME_COLORS) as Array<keyof typeof THEME_COLORS>;

export const ARC_TO_PATTERN_MAP: Record<string, string> = {
  learning: "lightbulb_wave",
  discovery: "lightbulb_wave",
  selling: "target_ring",
  story: "target_ring",
  validation: "puzzle_flow",
  experiments: "puzzle_flow",
  building: "rocket_grid",
  launching: "rocket_grid",
  growth: "star_burst",
};

export function inferPatternFromArc(arcCategory?: string | null): string | null {
  if (!arcCategory) return null;
  const normalized = arcCategory.toLowerCase().trim();
  return ARC_TO_PATTERN_MAP[normalized] ?? null;
}

// ============================================================================
// STORY BADGE CATALOG — Batch 5
// ============================================================================

export interface StoryBadgeDef {
  id: string;
  label: string;
  emoji: string;
  description: string;
  unlockDescription: string;
}

export const STORY_BADGE_CATALOG: Record<string, StoryBadgeDef> = {
  first_session: {
    id: "first_session",
    label: "First Rep",
    emoji: "✅",
    description: "Completed your first session",
    unlockDescription: "Complete at least 1 learning session",
  },
  first_note_created: {
    id: "first_note_created",
    label: "First Note",
    emoji: "📝",
    description: "Created your first note",
    unlockDescription: "Create at least 1 note",
  },
  note_taker_10: {
    id: "note_taker_10",
    label: "Note Taker",
    emoji: "📓",
    description: "Built a knowledge base of 10+ notes",
    unlockDescription: "Create at least 10 notes",
  },
  pathway_started: {
    id: "pathway_started",
    label: "Pathfinder",
    emoji: "🗺️",
    description: "Started your first learning arc",
    unlockDescription: "Start at least 1 learning pathway",
  },
  pathway_completed: {
    id: "pathway_completed",
    label: "Graduate",
    emoji: "🎓",
    description: "Completed a full learning arc",
    unlockDescription: "Complete at least 1 learning pathway",
  },
  rep_machine: {
    id: "rep_machine",
    label: "Rep Machine",
    emoji: "⚡",
    description: "Hit 10+ reps in a week",
    unlockDescription: "Reach 10+ reps in a single week or maintain a 3+ day streak",
  },
  streak_7: {
    id: "streak_7",
    label: "Week Warrior",
    emoji: "🔥",
    description: "Maintained a 7-day learning streak",
    unlockDescription: "Achieve a longest streak of at least 7 days",
  },
  chat_explorer: {
    id: "chat_explorer",
    label: "Deep Thinker",
    emoji: "💬",
    description: "Ran 5+ AI research sessions",
    unlockDescription: "Complete at least 5 chat sessions",
  },
};

export const STORY_BADGE_IDS = Object.keys(STORY_BADGE_CATALOG);

// ============================================================================
// SPRINT BADGE CATALOG — C3 Gamification
// ============================================================================

export type SprintBadgeType =
  | "FIRST_STEP"
  | "FIRST_LESSON"
  | "STREAK_3"
  | "PERFECT_SCORE"
  | "SPRINT_COMPLETE"
  | "AHEAD_OF_SCHEDULE"
  | "QUICK_LEARNER";

export interface SprintBadgeDef {
  type: SprintBadgeType;
  label: string;
  emoji: string;
  description: string;
  congratsText: string;
}

export const SPRINT_BADGE_CATALOG: Record<SprintBadgeType, SprintBadgeDef> = {
  FIRST_STEP: {
    type: "FIRST_STEP",
    label: "First Step",
    emoji: "👣",
    description: "Completed your first step in a sprint",
    congratsText: "You've taken your first step! Keep going.",
  },
  FIRST_LESSON: {
    type: "FIRST_LESSON",
    label: "Lesson Learner",
    emoji: "📖",
    description: "Completed your first lesson in a sprint",
    congratsText: "First lesson done! You're building momentum.",
  },
  STREAK_3: {
    type: "STREAK_3",
    label: "On Fire",
    emoji: "🔥",
    description: "Maintained a 3-day learning streak",
    congratsText: "3 days in a row! You're on fire.",
  },
  PERFECT_SCORE: {
    type: "PERFECT_SCORE",
    label: "Perfect Score",
    emoji: "💯",
    description: "Scored 100% on an assessment",
    congratsText: "Perfect score! You really know your stuff.",
  },
  SPRINT_COMPLETE: {
    type: "SPRINT_COMPLETE",
    label: "Sprint Champion",
    emoji: "🏆",
    description: "Completed an entire sprint",
    congratsText: "Sprint complete! Outstanding achievement.",
  },
  AHEAD_OF_SCHEDULE: {
    type: "AHEAD_OF_SCHEDULE",
    label: "Ahead of Schedule",
    emoji: "⚡",
    description: "Completed a sprint before the deadline",
    congratsText: "Finished early! You're ahead of the curve.",
  },
  QUICK_LEARNER: {
    type: "QUICK_LEARNER",
    label: "Quick Learner",
    emoji: "🚀",
    description: "Complete any lesson in under 3 minutes",
    congratsText: "Lightning fast! You're a quick learner.",
  },
};

export const SPRINT_BADGE_TYPES = Object.keys(SPRINT_BADGE_CATALOG) as SprintBadgeType[];
export const ALL_SPRINT_BADGES = Object.values(SPRINT_BADGE_CATALOG);

// ============================================================================
// HIGHLIGHT CARD TEMPLATES — Batch 5
// ============================================================================

export interface HighlightCardTemplateDef {
  id: string;
  title: string;
  emoji: string;
  subLabel: string;
}

export const HIGHLIGHT_CARD_TEMPLATES: Record<string, HighlightCardTemplateDef> = {
  notes_created: {
    id: "notes_created",
    title: "Notes created",
    emoji: "📝",
    subLabel: "All time",
  },
  sessions_total: {
    id: "sessions_total",
    title: "Sessions",
    emoji: "⚡",
    subLabel: "All time",
  },
  streak_best: {
    id: "streak_best",
    title: "Best streak",
    emoji: "🔥",
    subLabel: "Days",
  },
  arcs_completed: {
    id: "arcs_completed",
    title: "Arcs completed",
    emoji: "🎓",
    subLabel: "Completed",
  },
};

export const HIGHLIGHT_TEMPLATE_IDS = Object.keys(HIGHLIGHT_CARD_TEMPLATES);
export const DEFAULT_HIGHLIGHT_IDS = ["notes_created", "sessions_total", "arcs_completed"];

// ============================================================================
// LOADOUT AGENT CATALOG — Batch 6
// ============================================================================

export interface LoadoutAgentDef {
  id: string;
  name: string;
  description: string;
  iconName: string;
  category: "mode" | "tool";
}

export const LOADOUT_AGENT_CATALOG: Record<string, LoadoutAgentDef> = {
  mode_search: {
    id: "mode_search",
    name: "Search",
    description: "Quick lookup and definitions",
    iconName: "Search",
    category: "mode",
  },
  mode_research: {
    id: "mode_research",
    name: "Research",
    description: "Deep dives and analysis",
    iconName: "BookOpen",
    category: "mode",
  },
  mode_notes: {
    id: "mode_notes",
    name: "Notes",
    description: "Capture and organise",
    iconName: "StickyNote",
    category: "mode",
  },
  mode_learning: {
    id: "mode_learning",
    name: "Learning",
    description: "Explanations and quizzes",
    iconName: "GraduationCap",
    category: "mode",
  },
  mode_application: {
    id: "mode_application",
    name: "Application",
    description: "Drafts, plans and SOPs",
    iconName: "Briefcase",
    category: "mode",
  },
  mode_workflow: {
    id: "mode_workflow",
    name: "Workflow",
    description: "Step-by-step process design",
    iconName: "GitBranch",
    category: "mode",
  },
  quiz_me: {
    id: "quiz_me",
    name: "Quiz Me",
    description: "Test your understanding",
    iconName: "HelpCircle",
    category: "tool",
  },
  summarize_this: {
    id: "summarize_this",
    name: "Summarize",
    description: "Condense key points",
    iconName: "AlignLeft",
    category: "tool",
  },
  create_sop: {
    id: "create_sop",
    name: "Create SOP",
    description: "Build a process document",
    iconName: "ListChecks",
    category: "tool",
  },
  draft_email: {
    id: "draft_email",
    name: "Draft Email",
    description: "Write from context",
    iconName: "Mail",
    category: "tool",
  },
  what_should_i_do_next: {
    id: "what_should_i_do_next",
    name: "Next Step",
    description: "Get a clear next action",
    iconName: "ChevronRight",
    category: "tool",
  },
  extract_key_points: {
    id: "extract_key_points",
    name: "Key Points",
    description: "Pull out what matters",
    iconName: "Star",
    category: "tool",
  },
};

export const LOADOUT_AGENT_IDS = Object.keys(LOADOUT_AGENT_CATALOG);
export const DEFAULT_LOADOUT_IDS = ["mode_learning", "mode_research", "mode_notes"];
